/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Link as MuiLink,
  styled,
} from "@mui/material";
import { ElementType } from "react";
import { Link as RouterLink } from "react-router-dom";

import { TestIds } from "../testIds";

import MaterialSymbol from "./MaterialSymbol";
import Typography from "./Typography";

const Link = styled(MuiLink)<{ component?: ElementType; to?: string }>(({ theme }) => ({
  ...theme.typography.body2,
  color: colors.primary500,
  fontWeight: "bold",
}));

interface BreadcrumbsProps extends MuiBreadcrumbsProps {
  items: {
    text: string;
    href?: string;
  }[];
}
export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items, ...props }: BreadcrumbsProps) => {
  if (items.length === 0) {
    return null;
  }

  const linkItems = items.slice(0, -1).filter((item) => item.text !== "");
  const currentItem = items[items.length - 1];

  return (
    <MuiBreadcrumbs
      separator={<MaterialSymbol>chevron_right</MaterialSymbol>}
      aria-label="breadcrumb"
      data-testid={TestIds.components.breadcrumbs.container}
      {...props}
    >
      {linkItems.map((item) => (
        <Link
          component={RouterLink}
          underline="hover"
          to={item.href}
          key={item.text}
          id={TestIds.components.breadcrumbs.item(item.text)}
        >
          {item.text}
        </Link>
      ))}

      <Typography variant="body2" fontWeight={700} color="primary500" aria-current="page">
        {currentItem.text}
      </Typography>
    </MuiBreadcrumbs>
  );
};
